/**
 * NL-041 standalone Package Configurator adapter.
 *
 * The configurator and the Funnel Planner must never hold two rule sets. This
 * module is the configurator's half of that contract: it normalises the public
 * pricing page's context into the shared resolver's input and returns the
 * resolver's verdict unchanged. The display component may format the result but
 * must not re-decide it.
 *
 * Pure: no clock, no I/O, no environment reads, no state mutation.
 */
import {
  resolvePackageRecommendation,
  type PackageRecommendation,
  type PackageRecommendationTier,
} from "./package-recommender";

export const PACKAGE_CONFIGURATOR_SNAPSHOT_CONTRACT = "package-configurator-display-v1";

/**
 * The configurator's audience ids are derived from industry navigation hrefs
 * (`/agencies` -> `agencies`), while the shared resolver's complexity signals are
 * named in the singular (`agency`). Translating vocabularies is the adapter's
 * job, exactly as the planner adapter translates products into service ids.
 *
 * Without this, an agency buyer was scored Starter in the configurator and
 * Growth in the funnel for the same scope, and the configurator's own
 * `defaultAudienceId = "agency"` never matched any option.
 */
const AUDIENCE_ID_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  agencies: "agency",
  franchises: "franchise",
  creators: "creator",
});

export function normalizeConfiguratorAudienceId(audienceId: string | undefined): string | undefined {
  if (!audienceId) return undefined;

  return AUDIENCE_ID_ALIASES[audienceId] ?? audienceId;
}

export type ConfiguratorPricingContext = {
  /** The market the displayed bands belong to. */
  readonly market: string;
  /** The currency the displayed bands are denominated in. */
  readonly currency: string;
  /** FX date when the bands are an approximate local-currency conversion. */
  readonly rateDate?: string;
  readonly bands: Record<PackageRecommendationTier, string>;
};

export type ConfiguratorAdviceInput = {
  readonly audienceId?: string;
  readonly serviceIds?: readonly string[];
  readonly addOnIds?: readonly string[];
  readonly packageOverride?: PackageRecommendationTier;
  readonly pricing?: ConfiguratorPricingContext;
};

/**
 * The version is provenance, not decoration: it names the contract, the currency
 * the bands are denominated in, and the FX date when they are a conversion. An
 * old recommendation stays explainable after a pricing or rate change.
 */
export function buildConfiguratorPricingSnapshotVersion(currency: string, rateDate?: string): string {
  return [PACKAGE_CONFIGURATOR_SNAPSHOT_CONTRACT, currency, rateDate]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .map((part) => part.trim())
    .join(":");
}

/**
 * Resolves the configurator's recommendation through the shared resolver.
 *
 * An incomplete pricing context is not papered over: the resolver reports
 * `manualQuoteRequired`, and no band is returned. There is no fallback that
 * would display a price the contract declined to stand behind.
 */
export function resolveConfiguratorPackageAdvice(input: ConfiguratorAdviceInput): PackageRecommendation {
  const pricing = input.pricing;
  const audienceId = normalizeConfiguratorAudienceId(input.audienceId);

  return resolvePackageRecommendation({
    ...(audienceId ? { audienceId } : {}),
    ...(input.serviceIds ? { serviceIds: input.serviceIds } : {}),
    ...(input.addOnIds ? { addOnIds: input.addOnIds } : {}),
    ...(input.packageOverride ? { packageOverride: input.packageOverride } : {}),
    ...(pricing
      ? {
          market: pricing.market,
          currency: pricing.currency,
          pricingSnapshot: {
            version: buildConfiguratorPricingSnapshotVersion(pricing.currency, pricing.rateDate),
            bands: pricing.bands,
          },
        }
      : {}),
  });
}
